import { useCallback, useRef, useState } from "react";
import { Input } from "../input";
import { FormField } from "./FormField";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

type MaskType = "currency" | "percentage" | "phone" | "custom" | "none";

type NumberInputFieldProps = {
  label: string;
  name: string;
  placeholder?: string;
  defaultValue?: number;
  value?: number;
  required?: boolean;
  helperText?: string;
  error?: string;
  className?: string;
  icon?: any;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  allowNegative?: boolean;
  onChange?: (value: number | undefined) => void;
  disabled?: boolean;
  hidden?: boolean;
  pattern?: string;
  oninput?: (value: number | undefined) => void;
  mask?: MaskType;
  currencySymbol?: string;
  thousandsSeparator?: string;
  decimalSeparator?: string;
  customMask?: (value: string) => string;
  prefix?: string;
  suffix?: string;
  maxLength?: number;
  forceStepper?: boolean;
};

export function NumberInputField({
  label,
  name,
  placeholder,
  defaultValue,
  value,
  required = false,
  helperText,
  error,
  className,
  icon,
  tooltip,
  min,
  max,
  step = 1,
  decimals = 2,
  allowNegative = true,
  onChange,
  disabled,
  hidden,
  pattern,
  oninput,
  mask = "none",
  currencySymbol = "$",
  thousandsSeparator = ",",
  decimalSeparator = ".",
  customMask,
  prefix = "",
  suffix = "",
  maxLength,
  forceStepper = false,
}: NumberInputFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");

  const cleanValue = (val: string): string => {
    return val.replace(/[^\d.-]/g, "");
  };

  const applyCurrencyMask = (val: string): string => {
    const cleaned = cleanValue(val);
    if (cleaned === "" || cleaned === "-") return cleaned;

    const numValue = parseFloat(cleaned);
    if (isNaN(numValue)) return "";

    const parts = Math.abs(numValue).toFixed(decimals).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    const formatted = parts.join(decimalSeparator);
    return `${numValue < 0 ? "-" : ""}${currencySymbol}${formatted}`;
  };

  const applyPercentageMask = (val: string): string => {
    const cleaned = cleanValue(val);
    if (cleaned === "" || cleaned === "-") return cleaned;

    const numValue = parseFloat(cleaned);
    if (isNaN(numValue)) return "";

    return `${numValue.toFixed(decimals)}%`;
  };

  const applyPhoneMask = (val: string): string => {
    const cleaned = val.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return [
        match[1] ? "(" : "",
        match[1],
        match[1] && match[2] ? ") " : "",
        match[2],
        match[2] && match[3] ? "-" : "",
        match[3],
      ].join("");
    }
    return val;
  };

  const applyMask = (val: string): string => {
    if (mask === "none") return val;

    switch (mask) {
      case "currency":
        return applyCurrencyMask(val);
      case "percentage":
        return applyPercentageMask(val);
      case "phone":
        return applyPhoneMask(val);
      case "custom":
        return customMask ? customMask(val) : val;
      default:
        return val;
    }
  };

  const getDisplayValue = (): string => {
    if (isEditing) {
      return localValue;
    }
    if (value !== undefined) {
      return applyMask(value.toString());
    }
    if (defaultValue !== undefined) {
      return applyMask(defaultValue.toString());
    }
    return "";
  };

  const displayValue = getDisplayValue();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Rechazar entrada si contiene letras (excepto para máscaras especiales)
    if (mask !== "phone" && /[a-zA-Z]/.test(inputValue)) {
      return;
    }

    const cleaned = cleanValue(inputValue);

    if (maxLength !== undefined) {
      const digitsOnly = cleaned.replace(/[^\d]/g, "");
      if (digitsOnly.length > maxLength) {
        return;
      }
    }

    setLocalValue(inputValue);

    if (cleaned === "" || cleaned === "-") {
      onChange?.(undefined);
      return;
    }

    let numValue = parseFloat(cleaned);

    if (isNaN(numValue)) {
      onChange?.(undefined);
      return;
    }

    if (!allowNegative && numValue < 0) {
      numValue = 0;
    }

    if (min !== undefined && numValue < min) {
      numValue = min;
    }
    if (max !== undefined && numValue > max) {
      numValue = max;
    }

    if (decimals !== undefined) {
      numValue = parseFloat(numValue.toFixed(decimals));
    }

    onChange?.(numValue);
  };

  const handleBlur = () => {
    setIsEditing(false);
    setLocalValue("");
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    const currentValue =
      value !== undefined
        ? value.toString()
        : defaultValue !== undefined
          ? defaultValue.toString()
          : "";
    setLocalValue(currentValue);
    setTimeout(() => e.target.select(), 0);
  };

  const stepValue = useCallback(
    (direction: 1 | -1) => {
      const current = value !== undefined ? value : (defaultValue ?? 0);
      let next = current + direction * (step ?? 1);
      if (min !== undefined) next = Math.max(min, next);
      if (max !== undefined) next = Math.min(max, next);
      next = parseFloat(next.toFixed(decimals));
      onChange?.(next);
    },
    [value, step, min, max, decimals, defaultValue, onChange],
  );

  const isMobile = forceStepper || window.innerWidth < 768;

  const innerInput = (
    <Input
      ref={inputRef}
      id={name}
      name={name}
      type={mask === "phone" ? "tel" : "text"}
      pattern={pattern}
      placeholder={placeholder}
      value={displayValue}
      required={required}
      className={cn(
        className,
        isMobile &&
          mask !== "phone" &&
          "border-none shadow-none rounded-none focus-visible:ring-0 text-center h-9",
      )}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={disabled}
      hidden={hidden}
      inputMode={mask === "phone" ? "tel" : "decimal"}
    />
  );

  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
      disabled={disabled}
      hidden={hidden}
      pattern={pattern}
    >
      {isMobile && mask !== "phone" ? (
        <div
          className={cn(
            "flex items-center rounded-[var(--radius)] border border-ring/50 overflow-hidden",
            "shadow-[0_4px_16px_rgba(0,0,0,0.1)]",
            disabled && "opacity-50 pointer-events-none",
          )}
        >
          <button
            type="button"
            onClick={() => stepValue(-1)}
            disabled={
              disabled ||
              (min !== undefined &&
                parseFloat(cleanValue(displayValue) || "0") <= min)
            }
            className="h-9 w-9 flex items-center justify-center bg-muted/60 hover:bg-muted active:bg-muted/40 border-r border-ring/30 flex-shrink-0 text-muted-foreground transition-colors disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          {innerInput}
          <button
            type="button"
            onClick={() => stepValue(1)}
            disabled={
              disabled ||
              (max !== undefined &&
                parseFloat(cleanValue(displayValue) || "0") >= max)
            }
            className="h-9 w-9 flex items-center justify-center bg-muted/60 hover:bg-muted active:bg-muted/40 border-l border-ring/30 flex-shrink-0 text-muted-foreground transition-colors disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        innerInput
      )}
    </FormField>
  );
}
